import { 
    Trash2, 
    Pen, 
    Calendar,
    MessageSquare,
    Target
} from 'lucide-react';

type GoalReviewEntryProps = {
    review: any;
    setEditing: (id: string) => void;
    remove: (id: string) => void;
};

const progressColors = {
    red: '#ef4444',
    amber: '#f59e0b',
    green: '#10b981',
};

const progressLabels = {
    red: 'Behind',
    amber: 'On Track',
    green: 'Ahead',
};

const GoalReviewEntry: React.FC<GoalReviewEntryProps> = ({ review, setEditing, remove }) => {
    const getProgressConfig = (rag: string) => {
        switch(rag) {
            case 'red':
                return {
                    color: 'from-red-500 to-red-600',
                    bgColor: 'bg-red-50',
                    textColor: 'text-red-700',
                    dotColor: progressColors.red,
                    label: progressLabels.red
                };
            case 'amber':
                return {
                    color: 'from-amber-500 to-amber-600',
                    bgColor: 'bg-amber-50',
                    textColor: 'text-amber-700',
                    dotColor: progressColors.amber,
                    label: progressLabels.amber
                };
            case 'green':
                return {
                    color: 'from-green-500 to-green-600',
                    bgColor: 'bg-green-50',
                    textColor: 'text-green-700',
                    dotColor: progressColors.green,
                    label: progressLabels.green
                };
            default:
                return {
                    color: 'from-gray-500 to-gray-600',
                    bgColor: 'bg-gray-50',
                    textColor: 'text-gray-700',
                    dotColor: '#6b7280',
                    label: 'Not Set'
                };
        }
    };

    const progressConfig = getProgressConfig(review.rag);

    const handleDeleteClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        remove(review.id);
    };

    return (
        <div 
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl mb-4 cursor-pointer"
            onClick={() => setEditing(review.id)}
        >
            {/* Header */}
            <div className={`bg-gradient-to-r ${progressConfig.color} p-4 text-white relative`}>
                <div className="absolute inset-0 bg-white opacity-10"></div>
                <div className="relative flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Target className="w-4 h-4" />
                        <div>
                            <h3 className="font-bold text-lg">
                                Goal Review
                            </h3>
                            
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleDeleteClick}
                            className="w-8 h-8 bg-red-500 bg-opacity-80 hover:bg-opacity-100 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4 text-white" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="p-6">
                {/* Review Date and Progress Status */}
                <div className="mb-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3 flex items-center space-x-3">
                            <Calendar className="w-5 h-5 text-blue-500" />
                            <div>
                                <div className="text-sm text-blue-600 font-medium">Review Date</div>
                                <div className="text-lg font-bold text-blue-800">
                                    {new Date(review.review_date).toLocaleDateString('en-GB')}
                                </div>
                            </div>
                        </div>

                        {/* Progress Status */}
                        {review.rag ? (
                            <div className={`${progressConfig.bgColor} rounded-lg p-3 flex items-center space-x-3`}>
                                <div 
                                    className="w-4 h-4 rounded-full" 
                                    style={{ backgroundColor: progressConfig.dotColor }}
                                ></div>
                                <div>
                                    <div className={`text-sm font-medium ${progressConfig.textColor.replace('text-', 'text-').replace('-700', '-600')}`}>
                                        Progress Status
                                    </div>
                                    <div className={`text-lg font-bold ${progressConfig.textColor}`}>
                                        {progressConfig.label}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                                <div className="w-4 h-4 rounded-full bg-gray-400"></div>
                                <div>
                                    <div className="text-sm font-medium text-gray-600">
                                        Progress Status
                                    </div>
                                    <div className="text-lg font-bold text-gray-700">
                                        Not Set
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Review Content */}
                <div className="space-y-4">
                    {/* Review Text */}
                    <div className="bg-indigo-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                            <MessageSquare className="w-5 h-5 text-indigo-600" />
                            <span className="font-semibold text-indigo-800 text-sm">Review Notes</span>
                        </div>
                        <p className="text-base text-indigo-700 leading-relaxed">
                            {review.review}
                        </p>
                    </div>
                </div>

                {/* Edit indicator */}
                <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-100 text-gray-500 hover:text-gray-700 transition-colors duration-200">
                    <Pen className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Click to Edit</span>
                </div>
            </div>
        </div>
    );
};

export default GoalReviewEntry;